import React, { useState } from 'react'

import colors from './helpers/colors'
import FiltersListForPrintView from './FiltersListForPrintView'
import LilCard from './LilCard'
import LitAreaChart from './visualizations/LitAreaChart'
import removeEmpty from './helpers/removeEmpty'

// Data points for trends
const trendKeys = ['applied', 'admitted', 'deposited', 'housing', 'orientation']

const toAcademicYear = term =>
  `${term.slice(0, 4)}-${parseInt(term.slice(0, 4)) + 1}`

const toCumul = (data, keys = trendKeys, asOfDates) => {
  const cumulObject = keys.reduce((o, k) => ({ ...o, [k]: 0 }), {})
  return data.map(d => {
    keys.forEach(
      (k, i) =>
        (cumulObject[k] =
          asOfDates && asOfDates[i] <= d.id ? NaN : cumulObject[k] + (d[k] || 0))
    )
    return { ...d, ...cumulObject }
  })
}

function Trends({ filters, setFilterOptions }) {
  const [trendChart, setTrendChart] = useState(0)
  const [data, setData] = useState()
  const [cumulativeTerm, setCumulativeTerm] = useState('')

  const handleInput = event => {
    setCumulativeTerm(event.target.value)
  }

  React.useEffect(() => {
    fetch(`${window.__api}/latest/byDay`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filters: removeEmpty(filters),
      }),
    })
      .then(res => res.json())
      .then(res => {
        setCumulativeTerm(res.terms[0])
        setData(res)
        setFilterOptions(res.filters)
      })
      .catch(err => {
        console.error(err)
      })
  }, [filters, setFilterOptions])

  const mergeByKey = React.useCallback(
    item => {
      const merged = {}

      data.terms.forEach(term =>
        data[term].forEach(d => {
          const key = `${2 -
            (parseInt(term.substr(0, 4)) - parseInt(d.date.substr(0, 4)))}-${
            d.day
          }`
          merged[key] = {
            id: key,
            date: d.day,
            ...(merged[key] || {}),
            [toAcademicYear(term)]: d[item],
          }
        })
      )

      return Object.keys(merged)
        .sort()
        .map(k => merged[k])
    },
    [data]
  )

  const trendCharts = React.useMemo(() => {
    if (!data || !data.terms) return []
    const academicYears = data.terms.map(t => toAcademicYear(t))
    const asOfDates = data.terms.map(
      t =>
        `${2 -
          (parseInt(t.substr(0, 4)) -
            parseInt(data.asOfDates[t].substr(0, 4)))}${data.asOfDates[
          t
        ].substr(4)}`
    )

    const defaultColors = academicYears.map((y, i) => ({
      key: y,
      color: colors.default[i],
    }))

    // All the single charts
    const singleCharts = trendKeys.map(x => ({
      name: `${x[0].toUpperCase() + x.slice(1)}`,
      color: colors[x][0],
      type: 'line',
      data: toCumul(mergeByKey(x), academicYears, asOfDates),
      dataKeys: defaultColors,
    }))

    // First the cumulative chart then the single item charts
    return [
      {
        name: 'Cumulative Overview',
        color: colors.cumulative[0],
        type: 'area',
        data: toCumul(data[cumulativeTerm]),
        dataKeys: trendKeys.map(cur => ({
          key: cur,
          color: colors[cur][0],
        })),
      },
      ...singleCharts,
    ]
  }, [data, cumulativeTerm, mergeByKey])

  if (!data) return <div>Loading...</div>

  return (
    <>
      <div className="main-header">
        <h2>{trendCharts[trendChart].name}</h2>
        {trendCharts[trendChart].name === 'Cumulative Overview' && (
          <div>
            <label htmlFor="Term Selector">Term: </label>
            <select
              onChange={handleInput}
              value={cumulativeTerm}
              name="Term Selector"
              style={{ marginTop: '-1em', marginBottom: '1em' }}
            >
              {data.terms.map((term, i) => (
                <option key={i} value={term}>
                  Fall {term.slice(0, 4)}
                </option>
              ))}
            </select>
          </div>
        )}
        <FiltersListForPrintView filters={filters} />
        <LitAreaChart
          height={480}
          type={trendCharts[trendChart].type}
          legend
          data={trendCharts[trendChart].data}
          xAxis="date"
          dataKeys={trendCharts[trendChart].dataKeys}
        />
      </div>
      <div className="main-overview">
        {trendCharts.map((item, i) => (
          <LilCard
            key={i}
            onClick={() => setTrendChart(i)}
            name={item.name}
            color={item.color}
          >
            <LitAreaChart
              height={100}
              tooltip={false}
              type={item.type}
              hide
              data={item.data}
              xAxis="date"
              dataKeys={item.dataKeys}
            />
          </LilCard>
        ))}
      </div>
    </>
  )
}

export default React.memo(Trends)
